import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Users, Plus, Trash2, ShieldCheck } from "lucide-react";
import { API, auth, inputClass, labelClass, primaryBtn, ghostBtn, fmtDate, handleAuthError } from "./shared";
import { useConfirm } from "./useConfirm";

const AdminTeam = ({ token, logout, me }) => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);
  const { confirm, dialog } = useConfirm();

  const load = () =>
    axios.get(`${API}/users`, auth(token)).then((r) => setItems(r.data)).catch((err) => handleAuthError(err, logout, () => {}));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/users`, form, auth(token));
      toast.success(`Compte créé pour ${form.email}`);
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      handleAuthError(err, logout, () => toast.error(typeof d === "string" ? d : "Échec de la création (mot de passe ≥ 8 caractères)"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    const ok = await confirm({ title: "Retirer ce responsable ?", description: `${u.email} ne pourra plus se connecter à l'espace entreprise.` , action: "Oui, retirer" });
    if (!ok) return;
    try {
      await axios.delete(`${API}/users/${u.id}`, auth(token));
      toast.success("Accès retiré");
      load();
    } catch (err) {
      handleAuthError(err, logout, () => toast.error(err.response?.data?.detail || "Échec"));
    }
  };

  return (
    <div data-testid="admin-team-tab">
      {dialog}
      <form onSubmit={add} className="rounded-3xl bg-mint p-8" data-testid="admin-team-form">
        <h2 className="font-display flex items-center gap-2 text-lg font-bold text-forest">
          <Users className="h-5 w-5 text-gold" />
          Ajouter un responsable
        </h2>
        <p className="mt-2 text-sm text-brand-hover">Chaque responsable se connecte avec son propre email et mot de passe.</p>
        <div className="mt-6 grid gap-5 sm:grid-cols-3">
          <div>
            <label className={labelClass}>Nom</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex : Zakaria Diallo" data-testid="admin-team-name-input" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Email *</label>
            <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="responsable@groupeautoluxeci.com" data-testid="admin-team-email-input" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Mot de passe * (8 caractères min.)</label>
            <input type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="admin-team-password-input" className={inputClass} />
          </div>
        </div>
        <button type="submit" disabled={saving} data-testid="admin-team-submit-button" className={`mt-6 ${primaryBtn}`}>
          <Plus className="h-4 w-4" />
          {saving ? "Création..." : "Créer le compte"}
        </button>
      </form>

      <h2 className="font-display mt-14 text-lg font-bold text-forest">
        Accès actifs <span className="text-gold">({items.length})</span>
      </h2>
      <div className="mt-6 grid gap-3 md:grid-cols-2">
        {items.map((u, i) => (
          <div key={u.id} className="flex items-center justify-between gap-4 rounded-2xl border border-brand/10 bg-white px-5 py-4" data-testid={`admin-team-user-${i}`}>
            <div className="flex items-center gap-4">
              <span className={`flex h-11 w-11 items-center justify-center rounded-xl text-white ${u.role === "admin" ? "bg-gold" : "bg-brand"}`}>
                {u.role === "admin" ? <ShieldCheck className="h-5 w-5" /> : <Users className="h-5 w-5" />}
              </span>
              <div>
                <p className="font-display text-base font-bold text-forest">{u.name || u.email}</p>
                <p className="text-xs text-brand">{u.email} · {u.role === "admin" ? "Administrateur principal" : "Responsable"}{u.created_at ? ` · ${fmtDate(u.created_at)}` : ""}</p>
              </div>
            </div>
            {u.id !== me?.id && u.role !== "admin" && (
              <button onClick={() => remove(u)} data-testid={`admin-team-user-${i}-delete`} className={`${ghostBtn} hover:border-gold hover:text-gold`}>
                <Trash2 className="h-3.5 w-3.5" /> Retirer
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminTeam;
